// Seller home — lists all in-flight listings the seller has started in this
// browser. Per the multi-listing model (state in localStorage for beta, will
// migrate to server-side seller_listings table once Supabase Auth lands).

import { useEffect, useState } from "react";
import Logo from "./Logo";
import { STAGES, STAGE_COUNT } from "../stages";
import { BETA_SECRET } from "../lib/api";

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

// Visual progress strip for a journey — one cell per stage.
function Progress({ journey }) {
  const stages = journey.stages || {};
  return (
    <div className="s-list-card-prog">
      {STAGES.map((s, i) => {
        const status = stages[s.id]?.status || "pending";
        const isCurrent = i === journey.currentStageIdx;
        const cls = isCurrent
          ? "current"
          : status === "done"
            ? "done"
            : status === "awaiting"
              ? "awaiting"
              : "";
        return <div key={s.id} className={`s-list-card-prog-cell ${cls}`} />;
      })}
    </div>
  );
}

const summarize = (journey) => {
  const stages = journey.stages || {};
  const done = STAGES.filter((s) => stages[s.id]?.status === "done").length;
  const cur = STAGES[journey.currentStageIdx]?.title || "Not started";
  return `${done}/${STAGE_COUNT} · current step: ${cur}`;
};

const fmtPrice = (cents) => cents == null ? "—" : `$${Math.round(cents / 100).toLocaleString()}`;

function OfferCard({ offer, onRespond }) {
  const [mode, setMode] = useState(null); // null | "counter"
  const [counterPrice, setCounterPrice] = useState("");
  const [counterNote, setCounterNote] = useState("");
  const [counterDate, setCounterDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [txLink, setTxLink] = useState(null);
  const [txCopied, setTxCopied] = useState(false);

  const respond = async (response) => {
    setBusy(true);
    try {
      const res = await fetch("/api/offers", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
        body: JSON.stringify({
          action: "respond",
          offer_id: offer.id,
          response,
          counter_price_cents: counterPrice ? Math.round(parseFloat(counterPrice.replace(/,/g, "")) * 100) : undefined,
          counter_note: counterNote || undefined,
          counter_closing_date: counterDate || undefined,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        onRespond(offer.id, data.status);
        if (data.buyer_link) setTxLink(data.buyer_link);
      }
    } finally { setBusy(false); }
  };

  const statusColor = { submitted: "#e6c97a", countered: "#7ab0e6", accepted: "#7ecfb0", declined: "#e67a7a", withdrawn: "#9bbfb2" };

  return (
    <div style={{ background: "#1a2922", border: "1px solid #1c2b25", borderRadius: 12, padding: 16, marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 20, fontFamily: "'Playfair Display',serif", fontWeight: 700, color: "#cfe6da" }}>{fmtPrice(offer.offer_price_cents)}</div>
          <div style={{ fontSize: 11, color: "#7aad98", fontFamily: "'DM Mono',monospace", marginTop: 2 }}>
            {offer.financing_type.toUpperCase()} · {offer.buyer_name}
          </div>
        </div>
        <span style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", padding: "3px 10px", borderRadius: 20, background: "#1c2b25", color: statusColor[offer.status] || "#9bbfb2", letterSpacing: "0.08em" }}>
          {offer.status}
        </span>
      </div>

      <div style={{ fontSize: 12, color: "#5a8a78", display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
        {offer.contingency_inspection && <span>inspection</span>}
        {offer.contingency_financing  && <span>financing</span>}
        {offer.contingency_appraisal  && <span>appraisal</span>}
        {offer.closing_date && <span>close {offer.closing_date}</span>}
        {offer.earnest_money_cents && <span>earnest {fmtPrice(offer.earnest_money_cents)}</span>}
      </div>

      {offer.buyer_note && (
        <div style={{ fontSize: 12, color: "#9bbfb2", fontStyle: "italic", marginBottom: 10, borderLeft: "2px solid #1c2b25", paddingLeft: 10 }}>
          "{offer.buyer_note}"
        </div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
        {offer.prequal_verified
          ? <span style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", background: "#1c5448", color: "#7ecfb0", padding: "2px 8px", borderRadius: 10 }}>
              ✓ Pre-approved {offer.prequal_amount_cents ? fmtPrice(offer.prequal_amount_cents) : ""}{offer.prequal_lender ? ` · ${offer.prequal_lender}` : ""}
            </span>
          : offer.financing_type !== "cash"
            ? <span style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", background: "#3a1a1a", color: "#e67a7a", padding: "2px 8px", borderRadius: 10 }}>
                Unverified
              </span>
            : null}
        {offer.financing_type === "cash" &&
          <span style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", background: "#1c2b25", color: "#7ecfb0", padding: "2px 8px", borderRadius: 10 }}>Cash</span>}
      </div>
      <div style={{ fontSize: 11, color: "#5a8a78", fontFamily: "'DM Mono',monospace", marginBottom: 10 }}>
        {offer.buyer_email} {offer.buyer_phone ? `· ${offer.buyer_phone}` : ""} · {fmtRelative(offer.created_at)}
      </div>

      {offer.status === "accepted" && txLink && (
        <div style={{ marginTop: 10, background: "#0a1e14", border: "1px solid #1c5448", borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: "#5a8a78", letterSpacing: "0.08em", marginBottom: 6 }}>TRANSACTION ROOM</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <a href={txLink.replace(/\?.*/, "")} target="_blank" rel="noreferrer"
              style={{ fontSize: 12, background: "#1c5448", color: "#7ecfb0", border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontFamily: "inherit", textDecoration: "none", fontWeight: 600 }}>
              Open room →
            </a>
            <button onClick={() => {
              const full = `${window.location.origin}${txLink}`;
              navigator.clipboard.writeText(full).then(() => { setTxCopied(true); setTimeout(() => setTxCopied(false), 2000); });
            }}
              style={{ fontSize: 12, background: "#1c2b25", color: "#7aad98", border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontFamily: "inherit" }}>
              {txCopied ? "Copied!" : "Copy buyer link"}
            </button>
          </div>
        </div>
      )}

      {(offer.status === "submitted" || offer.status === "countered") && (
        <>
          {mode === "counter" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  style={{ flex: 1, background: "#0a100c", border: "1px solid #1c2b25", color: "#cfe6da", borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit" }}
                  placeholder="Counter price (e.g. 850000)" value={counterPrice}
                  onChange={(e) => setCounterPrice(e.target.value)} inputMode="numeric"
                />
                <input
                  type="date"
                  style={{ flex: 1, background: "#0a100c", border: "1px solid #1c2b25", color: "#cfe6da", borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit" }}
                  value={counterDate} onChange={(e) => setCounterDate(e.target.value)}
                />
              </div>
              <textarea
                style={{ background: "#0a100c", border: "1px solid #1c2b25", color: "#cfe6da", borderRadius: 8, padding: "8px 10px", fontSize: 12, fontFamily: "inherit", minHeight: 56, resize: "vertical" }}
                placeholder="Note to buyer (optional)" value={counterNote}
                onChange={(e) => setCounterNote(e.target.value)}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button disabled={busy} onClick={() => respond("counter")}
                  style={{ flex: 1, background: "#4a3a1a", color: "#e6c97a", border: "1px solid #8a6a2a", borderRadius: 8, padding: "8px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                  Send counter
                </button>
                <button onClick={() => setMode(null)}
                  style={{ background: "transparent", color: "#5a8a78", border: "1px solid #1c2b25", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button disabled={busy} onClick={() => respond("accept")}
                style={{ flex: 1, background: "#1c5448", color: "#7ecfb0", border: "none", borderRadius: 8, padding: "9px", fontSize: 12, cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>
                Accept
              </button>
              <button disabled={busy} onClick={() => setMode("counter")}
                style={{ flex: 1, background: "#4a3a1a", color: "#e6c97a", border: "none", borderRadius: 8, padding: "9px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                Counter
              </button>
              <button disabled={busy} onClick={() => respond("decline")}
                style={{ flex: 1, background: "#3a1a1a", color: "#e67a7a", border: "none", borderRadius: 8, padding: "9px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                Decline
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OffersPanel({ address, onClose }) {
  const [offers, setOffers] = useState(null);
  const [err, setErr] = useState(null);

  const loadOffers = () => {
    setErr(null);
    fetch(`/api/offers?address=${encodeURIComponent(address)}`, {
      headers: { "x-beta-token": BETA_SECRET },
    })
      .then((r) => r.json())
      .then((d) => setOffers(d.offers || []))
      .catch((e) => setErr(e.message));
  };

  useEffect(loadOffers, [address]); // eslint-disable-line

  const handleRespond = (offerId, newStatus) => {
    setOffers((prev) => prev.map((o) => o.id === offerId ? { ...o, status: newStatus } : o));
  };

  const pending = (offers || []).filter((o) => o.status === "submitted" || o.status === "countered").length;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(10,16,12,0.8)", zIndex: 50, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ background: "#0e1410", border: "1px solid #1c2b25", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 540, maxHeight: "85vh", overflow: "auto", padding: "24px 20px 32px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: "#cfe6da", fontWeight: 700 }}>
              Offers {pending > 0 && <span style={{ fontSize: 13, fontFamily: "'DM Mono',monospace", background: "#1c5448", color: "#7ecfb0", padding: "2px 8px", borderRadius: 10, marginLeft: 8 }}>{pending} pending</span>}
            </div>
            <div style={{ fontSize: 11, color: "#5a8a78", fontFamily: "'DM Mono',monospace", marginTop: 2 }}>{address}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#5a8a78", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>

        {err && (
          <div style={{ textAlign: "center", padding: 16 }}>
            <div style={{ color: "#e67a7a", fontSize: 12, marginBottom: 10 }}>Error: {err}</div>
            <button type="button" className="s-btn amber" onClick={loadOffers}>Try again</button>
          </div>
        )}
        {!offers && !err && <div style={{ color: "#5a8a78", textAlign: "center", padding: 24 }}>Loading…</div>}
        {offers?.length === 0 && <div style={{ color: "#5a8a78", textAlign: "center", padding: 24, fontStyle: "italic" }}>No offers yet</div>}
        {offers?.map((o) => <OfferCard key={o.id} offer={o} onRespond={handleRespond} />)}
      </div>
    </div>
  );
}

export default function Dashboard({ listings, onOpen, onNew, onDelete, onHome }) {
  const [offersFor, setOffersFor] = useState(null);
  return (
    <div className="s-app">
      {offersFor && <OffersPanel address={offersFor} onClose={() => setOffersFor(null)} />}
      <header className="s-top">
        <div className="s-brand" onClick={onHome}>
          <Logo />
          <div className="s-brand-name">6zero.ai</div>
        </div>
        <button type="button" className="s-back ml-auto" onClick={onHome}>← home</button>
      </header>

      <div className="s-dash">
        <div className="s-dash-head">
          <div>
            <h1 className="s-dash-h1">
              Your listings.{" "}
              <em>One address, end-to-end.</em>
            </h1>
            <div className="s-dash-sub">
              {listings.length === 0
                ? "No listings yet — start your first."
                : `${listings.length} listing${listings.length === 1 ? "" : "s"} in progress.`}
            </div>
          </div>
          <button type="button" className="s-dash-new" onClick={onNew}>+ Start a new listing</button>
        </div>

        {listings.length === 0 ? (
          <div className="s-dash-empty">
            <h2>Nothing here yet</h2>
            <p>Start by entering the address of the property you want to sell.<br />We'll handle the rest.</p>
            <button type="button" className="s-dash-new" onClick={onNew}>+ Start a new listing</button>
          </div>
        ) : (
          listings.map((j) => (
            <button
              key={j.id}
              type="button"
              className="s-list-card"
              onClick={() => onOpen(j.id)}
            >
              <div className="s-list-card-body">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div className="s-list-card-addr">{j.address || "(no address)"}</div>
                  <span
                    role="button"
                    tabIndex={0}
                    title="View offers"
                    onClick={(e) => { e.stopPropagation(); setOffersFor(j.address); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setOffersFor(j.address); } }}
                    style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", background: "#1c2b25", color: "#7aad98", padding: "2px 8px", borderRadius: 10, cursor: "pointer", flexShrink: 0, letterSpacing: "0.06em" }}
                  >
                    offers
                  </span>
                </div>
                <div className="s-list-card-meta">
                  Last opened {fmtRelative(j.last_opened_at)} · {summarize(j)}
                </div>
                <Progress journey={j} />
              </div>
              <span
                role="button"
                tabIndex={0}
                className="s-list-card-del"
                onClick={(e) => { e.stopPropagation(); onDelete(j.id); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onDelete(j.id); } }}
                title="Delete this listing"
              >×</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
